import { Link } from "react-router-dom";
import { Plus, Calendar, Clock, LogOut } from "lucide-react";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/schedora/StatusBadge";
import { SchedoraLogo } from "@/components/schedora/SchedoraLogo";
import { useApp } from "@/context/AppContext";
import { buildAppointmentView } from "@/lib/booking";
import { useWelcome } from "@/hooks/use-welcome";

export default function CustomerDashboard() {
  const { state, currentUser, currentBusiness, cancelAppointment, logout } = useApp();
  const welcome = useWelcome();
  const clientId = currentUser?.clientId;
  const businessId = currentBusiness?.id;
  const appointments = state.appointments
    .filter((entry) => entry.clientId === clientId && entry.businessId === businessId)
    .map((appointment) =>
      buildAppointmentView({
        appointment,
        clients: state.clients,
        services: state.services,
        staff: state.staff,
      }),
    )
    .sort((left, right) => `${left.date}-${left.startMinutes}`.localeCompare(`${right.date}-${right.startMinutes}`));

  const upcoming = appointments.filter((entry) => entry.status === "confirmed" || entry.status === "pending");
  const past = appointments.filter((entry) => entry.status === "completed" || entry.status === "cancelled");

  return (
    <div className="min-h-screen bg-background">
      <nav className="sticky top-0 z-50 border-b bg-background/80 backdrop-blur-md">
        <div className="container flex h-14 items-center justify-between">
          <SchedoraLogo iconClassName="h-5 w-5" />
          <div className="flex items-center gap-4 text-sm font-medium">
            <Link to="/customer/dashboard" className="text-foreground">
              Home
            </Link>
            <Link to="/customer/booking" className="text-muted-foreground hover:text-foreground">
              Book
            </Link>
            <Link to="/login" onClick={logout} className="text-muted-foreground hover:text-foreground">
              <LogOut className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </nav>

      <div className="container py-8 max-w-2xl">
        {welcome?.role === "client" && (
          <div className="mb-6 rounded-xl border bg-card p-5 shadow-soft">
            <p className="text-sm font-semibold text-foreground">Welcome to Schedora, {welcome.name}.</p>
            <p className="mt-1 text-sm text-muted-foreground">Your account is ready. Book your first appointment whenever you’re ready.</p>
          </div>
        )}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-heading font-bold text-foreground">Hello, {currentUser?.displayName ?? "Client"}</h1>
            <p className="text-sm text-muted-foreground">Here's what's coming up</p>
          </div>
          <Link to="/customer/booking">
            <Button size="sm">
              <Plus className="mr-1 h-4 w-4" /> Book Now
            </Button>
          </Link>
        </div>

        <section className="mb-8">
          <h2 className="text-lg font-heading font-semibold mb-3">Upcoming Appointments</h2>
          <div className="space-y-3">
            {upcoming.map((a) => (
              <div key={a.id} className="rounded-xl border bg-card p-5 shadow-soft">
                <div className="flex items-start justify-between">
                  <div>
                    <h4 className="font-semibold">{a.serviceName}</h4>
                    <p className="text-sm text-muted-foreground mt-1">with {a.staffName}</p>
                  </div>
                  <StatusBadge status={a.status} />
                </div>
                <div className="mt-3 flex items-center gap-4 text-sm text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <Calendar className="h-3.5 w-3.5" />
                    {a.date}
                  </span>
                  <span className="flex items-center gap-1">
                    <Clock className="h-3.5 w-3.5" />
                    {a.timeLabel}
                  </span>
                </div>
                <div className="mt-3 flex gap-2">
                  <Link to={`/customer/booking?appointmentId=${a.id}`}>
                    <Button size="sm" variant="outline">
                      Reschedule
                    </Button>
                  </Link>
                  <Button size="sm" variant="ghost" className="text-destructive" onClick={() => cancelAppointment(a.id)}>
                    Cancel
                  </Button>
                </div>
              </div>
            ))}
            {upcoming.length === 0 && (
              <div className="rounded-xl border border-dashed bg-card p-6 text-sm text-muted-foreground">
                No upcoming appointments yet.
              </div>
            )}
          </div>
        </section>

        <section>
          <h2 className="text-lg font-heading font-semibold mb-3">Past Appointments</h2>
          <div className="space-y-3">
            {past.map((a) => (
              <div key={a.id} className="rounded-xl border bg-card p-4 flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">{a.serviceName}</p>
                  <p className="text-xs text-muted-foreground">
                    {a.date} · {a.staffName}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <StatusBadge status={a.status} />
                  {a.status === "completed" && (
                    <Link to={`/customer/booking?serviceId=${a.serviceId}`}>
                      <Button size="sm" variant="outline">Rebook</Button>
                    </Link>
                  )}
                </div>
              </div>
            ))}
            {past.length === 0 && (
              <div className="rounded-xl border border-dashed bg-card p-6 text-sm text-muted-foreground">
                Your completed and cancelled bookings will show up here.
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}
